/**
 * Design tokens — typed mirror of the Blue Partner design system.
 *
 * **Provenance:** `design/design-system/tokens/*.css`. Those CSS files are the
 * upstream definition, vendored verbatim from the Claude Design export. This
 * file mirrors them so components get literal types, autocomplete, and a real
 * fallback value from `token()`. If the two ever disagree, **the CSS is right**
 * — fix this file, not the CSS.
 *
 * Read `design/design-system/readme.md` before adding anything here. The short
 * version: a near-black navy command room, one saturated blue (`#4a90e2`) for
 * anything actionable, amber (`#f59e0b`) reserved for time and "active", and
 * colour otherwise rationed hard.
 *
 * **There are two palettes now** (ADR-0064), and only one of them is a mirror.
 * `commandPalette` is upstream's and the paragraph above is about it;
 * `daylightPalette` is authored in this repo from the reference mockup's light
 * theme, because upstream exports no light mode. The rationing rules are the
 * same in both — one blue, amber for time, colour spent sparingly. What changes
 * is which direction the surface ladder runs.
 *
 * Components consume tokens through CSS custom properties — never a raw colour
 * or pixel value. ESLint enforces this (`no-restricted-syntax` in
 * `eslint.base.mjs`, ported from the upstream adherence config).
 */

/**
 * The two palettes, named for the surface they describe rather than for a
 * switch position: `dark` is the command room, `light` is daylight.
 *
 * **This used to be a one-member list, and the change is ADR-0064's.** Upstream
 * still exports one palette, so `daylightPalette` below is authored here, from
 * the light theme in `design/reference/blue-partner-files.mockup.html` — the
 * same prototype that lane is otherwise superseded framing for. `commandPalette`
 * remains the mirror of `design/design-system/tokens/*.css` and remains the one
 * the CSS is authoritative over; nothing about that changed.
 *
 * The names are `dark`/`light` and not `command`/`daylight` so that one
 * vocabulary reaches `color-scheme`, `prefers-color-scheme`, `data-theme`, and
 * the switcher on the General settings panel. A second set of words for the same
 * two states is a translation layer nobody needs.
 */
export const THEME_MODES = ['dark', 'light'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

/**
 * What a person chose, as opposed to what got resolved. `system` is the third
 * option on the switcher and is not a palette — it defers to the OS, which means
 * it can change while the app is open (`ThemeProvider` listens).
 */
export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** Where the chosen preference is remembered. One key, one owner. */
export const THEME_STORAGE_KEY = 'bp-theme';

/**
 * Token names, without the `--` prefix. Adding a name here forces the palette
 * below to define it — that is the point of the shape.
 */
export const TOKEN_NAMES = [
  // Brand / accent
  'bp-blue',
  'bp-blue-hover',
  'bp-blue-bright',
  'bp-blue-deep',
  'bp-blue-rail',
  'bp-blue-text',

  // Surfaces, back to front
  'bp-app',
  'bp-sidebar',
  'bp-rail',
  'bp-panel',
  'bp-card',
  'bp-input',
  'bp-elevated',

  // Borders
  'bp-border',
  'bp-border-soft',
  'bp-border-strong',

  /*
    Ink and paper — the one pair that does not follow the theme (USR-13).

    Everything else here describes the command room, which is dark by decision
    and has no light mode. These two describe a **document**: a signature is
    drawn to be flattened onto a white page, so it is dark ink on light paper
    wherever it appears. Rendering the pad in `bp-text` would capture a
    near-white mark that is perfectly legible in the app and invisible the
    moment it lands on a filing — a bug with no symptom until it reaches paper.

    They are not a light mode and must not become the seed of one.
  */
  'bp-ink',
  'bp-paper',

  // Text
  'bp-text',
  'bp-text-secondary',
  'bp-text-muted',
  'bp-text-tertiary',
  'bp-text-placeholder',
  'bp-text-faint',

  // Signal + status
  'bp-amber',
  'bp-danger',
  'bp-success',
  'bp-warning',

  // Derived — see the note on `commandPalette`
  'bp-on-accent',
  'bp-scrim',

  // Radii
  'radius-sm',
  'radius-md',
  'radius-lg',
  'radius-xl',
  'radius-2xl',
  'radius-3xl',
  'radius-full',

  // Spacing
  'space-xs',
  'space-sm',
  'space-md',
  'space-lg',
  'space-xl',
  'space-2xl',

  // Effects
  'shadow-sm',
  'shadow-md',
  'shadow-lg',
  'shadow-xl',
  'glow-blue',
  'glow-blue-soft',
  'glow-danger',

  // Layout constants
  'rail-width',
  'sidebar-width',
  'content-max',
  'rail-indicator',
] as const;

export type TokenName = (typeof TOKEN_NAMES)[number];

/** A complete palette. Every token must be present. */
export type Palette = Record<TokenName, string>;

/**
 * The command palette. Values are copied from
 * `design/design-system/tokens/{colors,spacing,effects}.css`.
 */
export const commandPalette: Palette = {
  // --- Brand / accent -------------------------------------------------
  'bp-blue': '#4a90e2', // primary signal accent
  'bp-blue-hover': '#3a7bc2', // pressed / hover-darker on fills
  'bp-blue-bright': '#5da0eb', // hover-lighter on solid buttons
  'bp-blue-deep': '#2d4c6b', // selected row wash
  'bp-blue-rail': '#6fa1ff', // active rail indicator / timeline dot
  'bp-blue-text': '#73a7ff', // active nav label text

  // --- Surfaces (back → front) ----------------------------------------
  'bp-app': '#0a1120', // deepest canvas behind everything
  'bp-sidebar': '#0f172a',
  'bp-rail': '#151a22', // icon rail / inputs
  'bp-panel': '#101624', // modals & floating panels
  'bp-card': '#1e293b', // raised card
  'bp-input': '#151a22',
  'bp-elevated': '#1a2230', // active sidebar item

  // --- Borders / hairlines --------------------------------------------
  'bp-border': '#242d3d',
  'bp-border-soft': 'rgba(255, 255, 255, 0.03)',
  'bp-border-strong': 'rgba(255, 255, 255, 0.1)',

  // --- Text -------------------------------------------------------------
  // Ink and paper (USR-13) — a document's colours, not the room's. See the
  // note beside these names above.
  'bp-ink': '#12161f',
  'bp-paper': '#f7f7f5',

  'bp-text': '#e0e0e0',
  'bp-text-secondary': '#a0a0a0',
  'bp-text-muted': '#7f8998',
  'bp-text-tertiary': '#94a3b8',
  'bp-text-placeholder': '#94a3b8',
  'bp-text-faint': '#666666',

  // --- Signal + status ---------------------------------------------------
  // Amber is reserved for time and "active" pulses. Do not use it as a
  // general highlight — it reads as metadata.
  'bp-amber': '#f59e0b',
  'bp-danger': '#ff4444',
  'bp-success': '#4caf50',
  'bp-warning': '#ff9800',

  // --- Derived ------------------------------------------------------------
  // Not in the upstream CSS, but required by components and unambiguous from
  // the readme. Flagged so a future re-export can promote or correct them.
  //   readme: "Solid blue buttons" render white label text.
  'bp-on-accent': '#ffffff',
  //   readme: modal/voice scrim is "command-app/95 + backdrop-blur-xl".
  'bp-scrim': 'rgba(10, 17, 32, 0.95)',

  // --- Radii (nothing is sharp-cornered) --------------------------------
  'radius-sm': '6px',
  'radius-md': '8px', // default buttons, small chips
  'radius-lg': '12px', // inputs, list rows
  'radius-xl': '16px', // nested tiles
  'radius-2xl': '24px', // cards, search
  'radius-3xl': '32px', // modals
  'radius-full': '9999px', // avatars, dots

  // --- Spacing (8px base) ------------------------------------------------
  'space-xs': '4px',
  'space-sm': '8px',
  'space-md': '16px',
  'space-lg': '24px',
  'space-xl': '32px',
  'space-2xl': '48px',

  // --- Effects -----------------------------------------------------------
  // Resting UI is shadowless. Shadows are for floating things; the signature
  // elevation cue is the blue glow on primary actions.
  'shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.3)',
  'shadow-md': '0 4px 12px rgba(0, 0, 0, 0.35)',
  'shadow-lg': '0 10px 30px rgba(0, 0, 0, 0.45)',
  'shadow-xl': '0 24px 60px rgba(0, 0, 0, 0.55)',
  'glow-blue': '0 8px 24px rgba(74, 144, 226, 0.2)',
  'glow-blue-soft': '0 4px 16px rgba(74, 144, 226, 0.1)',
  'glow-danger': '0 0 10px rgba(255, 68, 68, 0.5)',

  // --- Layout constants --------------------------------------------------
  'rail-width': '64px',
  'sidebar-width': '236px',
  'content-max': '1000px',
  // readme: the active nav item carries a "3px blue-rail left border".
  'rail-indicator': '3px',
};

/**
 * Daylight — the light palette (**ADR-0064**).
 *
 * **Authored here rather than mirrored**, which is the one thing to know about
 * it. `design/design-system/tokens/*.css` exports a single dark palette and
 * still does; these values come from the light theme in
 * `design/reference/blue-partner-files.mockup.html`, which is the only place
 * anybody has drawn this product on a light ground. If upstream ever exports a
 * light palette, that one wins and this becomes a mirror like its sibling.
 *
 * **Spread over `commandPalette`, deliberately.** Radii, spacing, and the layout
 * constants are not colours and do not vary by theme, so listing them again
 * would be two copies of one fact — and the override block below is then exactly
 * the set of tokens a theme *does* change, which is the useful thing to read.
 *
 * The two rules that shaped the values, because they are the ones a reviewer
 * should check anything new against:
 *
 * - **`bp-blue-deep` is the solid primary fill, not a wash.** Fifteen call sites
 *   pair it with `bp-on-accent` (white) — the mockup's own `blue-wash` (#eaf1fb)
 *   would put white text on near-white. So it takes the mockup's *blue*, and the
 *   two option lists that used it as a highlight wash now take `bp-on-accent`
 *   for their label, which reads better on both palettes than what they had.
 * - **Amber, danger, success, and warning all darken.** They are text and border
 *   colours as often as they are fills (`AppShell`'s banner, `StorageSurface`'s
 *   cap notice), and the dark palette's values land under 3:1 on a light ground.
 */
export const daylightPalette: Palette = {
  ...commandPalette,

  // --- Brand / accent -------------------------------------------------
  'bp-blue': '#2f6fbe',
  'bp-blue-hover': '#1f5fa8', // pressed / hover-darker on fills
  // Hover-*lighter* on solid buttons, so it moves the other way from `hover`
  // above. This is the command palette's own blue, which is genuinely the
  // lighter of the two — the one place the palettes share a value on purpose.
  'bp-blue-bright': '#4a90e2',
  'bp-blue-deep': '#2f6fbe', // solid primary fill — see the note above
  'bp-blue-rail': '#2f6fbe', // active rail indicator
  'bp-blue-text': '#1f5fa8', // active nav label text

  // --- Surfaces (back → front) ----------------------------------------
  // The ladder inverts: the canvas is the *tinted* one and the things raised
  // off it go white, where the command room raises by getting lighter.
  'bp-app': '#eef1f6',
  'bp-sidebar': '#ffffff',
  'bp-rail': '#ffffff',
  'bp-panel': '#ffffff',
  'bp-card': '#ffffff',
  'bp-input': '#ffffff',
  'bp-elevated': '#eaf1fb', // active sidebar item / hover

  // --- Borders / hairlines --------------------------------------------
  // Navy at low alpha rather than white: a hairline on a light ground is a
  // shadow of the ink, not a highlight.
  'bp-border': '#e2e7ef',
  'bp-border-soft': 'rgba(16, 32, 48, 0.05)',
  'bp-border-strong': 'rgba(16, 32, 48, 0.14)',

  // --- Text -------------------------------------------------------------
  // `bp-ink` and `bp-paper` are deliberately absent — they describe a document
  // rather than the room, and USR-13's note beside their names in `TOKEN_NAMES`
  // is the reason. A signature is dark ink on light paper in both themes.
  'bp-text': '#16202e',
  'bp-text-secondary': '#2b3a52',
  /*
    **Darker than the reference mockup's own `muted` (#7a879b), and that is a
    correction rather than a deviation.** Measured against these surfaces it
    lands at 3.64:1 on a card and 3.22:1 on the canvas — short of WCAG AA's
    4.5:1, and `bp-text-muted` is not decoration here: it is every inactive
    sidebar label, every `<Eyebrow>`, and every hint line under a control. The
    prototype was drawn, not audited.

    The ladder's *direction* is kept: tertiary and placeholder stay one step
    lighter than muted, exactly as they are one step lighter in the command
    room. Only the floor moved. Ratios on `bp-card` / `bp-app`:
    muted 5.76 / 5.09, tertiary 5.50 / 4.86.
  */
  'bp-text-muted': '#5a6778',
  'bp-text-tertiary': '#5c6a7e',
  // Identical to tertiary, as they are in the command palette.
  'bp-text-placeholder': '#5c6a7e',
  // Disabled and inactive icons — non-text, so 3:1 is the bar, and this clears
  // it at 3.03 on a card while still reading as switched off.
  'bp-text-faint': '#8a95a6',

  // --- Signal + status ---------------------------------------------------
  /*
    **All four are one step darker than the reference mockup's**, and it is the
    same correction as the muted ladder above rather than four separate
    judgement calls. Every one of these is *text* in this app — a timestamp, an
    `Active` badge, a `role="alert"` line, a cap notice — at 10 to 12px, on the
    canvas or on an elevated row. Measured against those grounds the mockup's
    values land at 4.42, 4.43, and 4.27:1, all just under AA's 4.5. Each moved
    the smallest step that clears it on the worst ground it appears on:

    | token   | mockup    | here      | on card / canvas / elevated |
    | ------- | --------- | --------- | --------------------------- |
    | amber   | `#b45309` | `#a94e08` | 5.56 / 4.91 / 4.89          |
    | success | `#15803d` | `#136c34` | 6.52 / 5.76 / 5.73          |
    | danger  | `#d92d20` | `#c9271b` | 5.54 / 4.89 / 4.87          |

    `warning` came in at `#c2410c` (5.18 / 4.57 / 4.56) and needed no move.
  */
  'bp-amber': '#a94e08',
  'bp-danger': '#c9271b',
  'bp-success': '#136c34',
  'bp-warning': '#c2410c',

  // --- Derived ------------------------------------------------------------
  'bp-on-accent': '#ffffff',
  'bp-scrim': 'rgba(238, 241, 246, 0.95)',

  // --- Effects -----------------------------------------------------------
  // Same ladder, navy instead of black and much lower alpha — a black shadow
  // on a light ground reads as dirt.
  'shadow-sm': '0 1px 2px rgba(16, 32, 48, 0.06)',
  'shadow-md': '0 4px 12px rgba(16, 32, 48, 0.1)',
  'shadow-lg': '0 10px 30px rgba(16, 32, 48, 0.12)',
  'shadow-xl': '0 24px 60px rgba(16, 32, 48, 0.16)',
  'glow-blue': '0 8px 24px rgba(47, 111, 190, 0.18)',
  'glow-blue-soft': '0 4px 16px rgba(47, 111, 190, 0.1)',
  'glow-danger': '0 0 10px rgba(217, 45, 32, 0.35)',
};

export const palettes: Record<ThemeMode, Palette> = {
  dark: commandPalette,
  light: daylightPalette,
};

/**
 * Typography scale. Mirrors `design/design-system/tokens/typography.css`.
 *
 * Body and table copy read large — this is built to be legible on a vehicle MDT
 * at a glance. Metadata is 10–12px uppercase with wide tracking.
 */
export const type = {
  fontSans: '"Inter", system-ui, -apple-system, sans-serif',
  fontMono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',

  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  size: {
    display: '48px',
    h1: '30px',
    h2: '24px',
    lg: '18px',
    base: '16px',
    sm: '14px',
    xs: '12px',
    '2xs': '10px',
  },

  leading: {
    tight: '1.1',
    snug: '1.35',
    relaxed: '1.6',
  },

  tracking: {
    tight: '-0.02em',
    normal: '0',
    label: '0.12em',
    wide: '0.2em',
    widest: '0.3em',
  },
} as const;

/** Motion. Loops are for *live* signals only, and respect reduced-motion. */
export const motion = {
  fast: '150ms ease-in-out',
  normal: '250ms ease-in-out',
  slow: '300ms cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

/**
 * CSS custom-property reference for a token, carrying the command value as an
 * inline fallback:
 *
 * ```ts
 * token('bp-card') // => 'var(--bp-card, #1e293b)'
 * ```
 *
 * The fallback is what lets any component render correctly with no
 * `ThemeProvider` ancestor — in isolation, in a test, in a preview. When the
 * provider is present its variables win.
 */
export function token(name: TokenName): string {
  return `var(--${name}, ${commandPalette[name]})`;
}
